import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { formatArticleDate } from "../utils/helpers";
import { InlineShareButtons } from "../components/ShareButton";
import MetaTags from "../components/MetaTags";
import SmallDatePicker from "../components/SmallDatePicker";
import PdfThumbnail from "../components/PdfThumbnail";

const EpaperPage = ({ allArticles }) => {
  const { articleId } = useParams();
  const [filter, setFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const pdfArticles = useMemo(() => {
    return allArticles
      .filter((a) => a.pdf)
      .sort((a, b) => {
        const aDate = a.publishedDate
          ? new Date(a.publishedDate)
          : new Date(a.createdAt);
        const bDate = b.publishedDate
          ? new Date(b.publishedDate)
          : new Date(b.createdAt);
        return bDate - aDate;
      });
  }, [allArticles]);

  const articlesToShow = useMemo(() => {
    const now = new Date();
    let filteredArticles;

    switch (filter) {
      case "today":
        const today = now.toDateString();
        filteredArticles = pdfArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d.toDateString() === today;
        });
        break;
      case "7days":
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        filteredArticles = pdfArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d >= sevenDaysAgo;
        });
        break;
      case "15days":
        const fifteenDaysAgo = new Date();
        fifteenDaysAgo.setDate(now.getDate() - 15);
        filteredArticles = pdfArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d >= fifteenDaysAgo;
        });
        break;
      case "all":
      default:
        filteredArticles = pdfArticles;
    }

    if (selectedDate) {
      const parts = selectedDate.split("/");
      if (parts.length === 3) {
        const [dd, mm, yyyy] = parts.map((s) => parseInt(s, 10));
        const target = new Date(yyyy, mm - 1, dd).toDateString();
        filteredArticles = filteredArticles.filter((article) => {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          return d.toDateString() === target;
        });
      }
    }

    if (debouncedQuery !== "") {
      const q = debouncedQuery.toLowerCase();

      const tryParseDDMMYYYY = (s) => {
        const parts = String(s).split("/");
        if (parts.length === 3) {
          const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
          if (!isNaN(dd) && !isNaN(mm) && !isNaN(yyyy))
            return new Date(yyyy, mm - 1, dd).toDateString();
        }
        return null;
      };

      const targetDateString = tryParseDDMMYYYY(q);

      return filteredArticles.filter((article) => {
        if (targetDateString) {
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          if (d.toDateString() === targetDateString) return true;
          if (article.date) {
            if (String(article.date).toLowerCase().includes(q)) return true;
          }
        }

        if (formatArticleDate(article).toLowerCase().includes(q)) return true;

        if (article.title && String(article.title).toLowerCase().includes(q))
          return true;

        if (article.pdf && String(article.pdf).toLowerCase().includes(q))
          return true;

        if (/^\d{4}$/.test(q)) {
          const year = q;
          const d = article.publishedDate
            ? new Date(article.publishedDate)
            : new Date(article.createdAt);
          if (String(d.getFullYear()) === year) return true;
        }

        return false;
      });
    }

    return filteredArticles;
  }, [filter, pdfArticles, debouncedQuery, selectedDate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  if (articleId) {
    const pdfArticle = allArticles.find((a) => a.id === parseInt(articleId));
    if (!pdfArticle || !pdfArticle.pdf) {
      return (
        <main className="flex-grow container mx-auto px-4 py-8 text-center">
          <MetaTags
            title="E-Paper Not Found"
            description="The requested e-paper could not be found or is not available."
          />
          <h1 className="text-3xl font-bold mb-6">E-Paper Not Found</h1>
          <p>The requested E-Paper could not be found or is not available.</p>
        </main>
      );
    }
    return (
      <main className="flex-grow container mx-auto px-4 py-8">
        <MetaTags
          title={`E-Paper: ${formatArticleDate(pdfArticle)}`}
          description={`Read the full e-paper from ${formatArticleDate(
            pdfArticle
          )}. Amdavad Post digital newspaper.`}
        />
        <h1 className="text-3xl font-bold mb-4 capitalize border-b-2 border-red-600 pb-2">
          E-Paper: {formatArticleDate(pdfArticle)}
        </h1>
        <div className="w-full h-[80vh] md:h-[100vh]">
          <iframe
            src={pdfArticle.pdf}
            title={`E-Paper for ${formatArticleDate(pdfArticle)}`}
            className="w-full h-full border-none"
          ></iframe>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      <MetaTags
        title="E-Paper Archives"
        description="Browse and read the archives of Amdavad Post e-papers. Access past editions of our newspaper."
      />
      <div className="flex flex-col md:flex-row justify-between items-center border-b-2 border-red-600 pb-2 mb-6 gap-4">
        <h1 className="text-3xl font-bold capitalize">E-Paper</h1>
        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Live search: title, date (dd/mm/yyyy), or filename"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="p-2 border rounded-md pl-8 w-full"
            />
            <svg
              className="w-5 h-5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="filter-select" className="font-semibold">
              Show:
            </label>
            <select
              id="filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="p-2 border rounded-md"
            >
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="15days">Last 15 Days</option>
            </select>
            <div className="flex items-center space-x-2 ml-4">
              <SmallDatePicker
                value={selectedDate}
                onChange={(v) => {
                  setSelectedDate(v);
                  setFilter("all");
                }}
              />
              {selectedDate && (
                <button
                  type="button"
                  className="text-sm text-gray-500 hover:text-gray-800"
                  onClick={() => setSelectedDate("")}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {articlesToShow.length > 0 ? (
          articlesToShow.map((article) => (
            <div
              key={article.id}
              className="group block bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300 relative"
            >
              <Link to={`/epaper/${article.id}`} className="block">
                <PdfThumbnail pdfUrl={article.pdf} />
                <div className="p-4">
                  <p className="font-bold text-lg">
                    {formatArticleDate(article)}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-blue-600 group-hover:underline">
                      View E-Paper
                    </span>
                    <InlineShareButtons
                      url={`${window.location.origin}/epaper/${article.id}`}
                      title={formatArticleDate(article)}
                    />
                  </div>
                </div>
              </Link>
            </div>
          ))
        ) : (
          <p className="col-span-full text-center py-8">
            No E-Papers found for the selected criteria.
          </p>
        )}
      </div>
    </main>
  );
};

export default EpaperPage;
